import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Eye, RefreshCw } from 'lucide-react';

const PREVIEW_SOURCE_LABELS = {
  sample_data: 'Amostra governada',
  runtime_document: 'Documento real',
};

const LAYOUT_SOURCE_LABELS = {
  layout_override: 'Override do builder',
  stored_template: 'Layout guardado',
};

export const TemplateBuilderRuntimePreviewCard = ({
  getLocationName,
  onOpenDialog,
  onRefreshPreview,
  previewError,
  previewLoading,
  previewMeta,
  previewUrl,
  selectedTemplate,
}) => {
  const effectiveLocationLabel = previewMeta?.runtimeLocationId
    ? getLocationName({ location_id: previewMeta.runtimeLocationId, location_name: previewMeta.runtimeLocationName || previewMeta.runtimeLocationId })
    : getLocationName(selectedTemplate);

  return (
    <div className="space-y-3" data-testid="template-builder-shell-runtime-preview-card">
      <div className="flex items-center justify-between rounded-xl border bg-white px-3 py-2">
        <div>
          <p className="text-sm font-semibold">Preview runtime / PDF</p>
          <p className="text-[11px] text-muted-foreground">Mesmo pipeline de `render-pdf` usado para validar builder, preview e PDF final.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={onRefreshPreview} disabled={previewLoading} data-testid="template-builder-shell-refresh-preview-button">
            <RefreshCw className={`mr-1 h-4 w-4 ${previewLoading ? 'animate-spin' : ''}`} /> Actualizar
          </Button>
          <Button size="sm" onClick={onOpenDialog} disabled={!previewUrl && !previewLoading} data-testid="template-builder-shell-open-preview-dialog-button">
            <Eye className="mr-1 h-4 w-4" /> Abrir grande
          </Button>
        </div>
      </div>

      <Card className="flex min-h-0 flex-col overflow-hidden" data-testid="template-builder-shell-runtime-preview-frame-card">
        <div className="grid gap-2 border-b bg-slate-50 px-3 py-2 md:grid-cols-2 xl:grid-cols-3" data-testid="template-builder-shell-runtime-preview-parity-grid">
          {[
            { key: 'pipeline', label: 'Pipeline', value: previewMeta?.renderPipeline === 'render_pdf_runtime_pipeline' ? 'Builder → render-pdf → PDF' : 'A aguardar' },
            { key: 'data-source', label: 'Dados', value: PREVIEW_SOURCE_LABELS[previewMeta?.dataSource] || 'A gerar' },
            { key: 'layout-source', label: 'Layout', value: LAYOUT_SOURCE_LABELS[previewMeta?.layoutSource] || 'A gerar' },
            { key: 'rule', label: 'Regra', value: previewMeta?.resolvedTemplateRule || 'A gerar' },
            { key: 'template', label: 'Template resolvido', value: previewMeta?.resolvedTemplateName || selectedTemplate?.name || 'Sem template' },
            { key: 'location', label: 'Filial efectiva', value: effectiveLocationLabel || 'Global' },
          ].map((item) => (
            <div key={item.key} className="rounded-lg border bg-white px-3 py-2" data-testid={`template-builder-shell-runtime-preview-meta-${item.key}`}>
              <Label className="text-[9px] uppercase tracking-[0.16em] text-muted-foreground">{item.label}</Label>
              <p className="mt-1 text-[11px] font-semibold leading-snug">{item.value}</p>
            </div>
          ))}
        </div>

        {(previewMeta?.printSettings?.pos_width || previewMeta?.printSettings?.print_copies) && (
          <div className="flex flex-wrap gap-2 border-b px-3 py-2" data-testid="template-builder-shell-runtime-preview-settings-strip">
            <Badge variant="outline">POS {previewMeta?.printSettings?.pos_width || '—'}</Badge>
            <Badge variant="outline">Vias {previewMeta?.printSettings?.print_copies || '—'}</Badge>
            <Badge variant="outline">QR {previewMeta?.printSettings?.show_qr_code ? 'Activo' : 'Off'}</Badge>
            <Badge variant="outline">Assinatura {previewMeta?.printSettings?.show_signature_line ? 'Activa' : 'Off'}</Badge>
          </div>
        )}

        <div className="min-h-[320px] flex-1 bg-white xl:h-[clamp(320px,36vh,520px)]" data-testid="template-builder-shell-runtime-preview-surface">
          {previewLoading ? (
            <div className="flex h-full items-center justify-center" data-testid="template-builder-shell-runtime-preview-loading">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-500 border-t-transparent" />
            </div>
          ) : previewUrl ? (
            <iframe src={previewUrl} title="Preview runtime PDF" className="h-full w-full" data-testid="template-builder-shell-runtime-preview-frame" />
          ) : (
            <div className="flex h-full items-center justify-center px-6 text-center text-sm text-muted-foreground" data-testid="template-builder-shell-runtime-preview-empty-state">
              {previewError || 'Sem preview disponível para este template.'}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};